import { Platform } from '@hotstar/platforms';
import { Storage } from '@hotstar/storage';
import type { DeviceInfo, PlatformAPI } from '@hotstar/platforms/types';
import type { MemoryInfo } from '@hotstar/analytics-event-schemas/api/feature/device/memory';
import { AppLogger, logErrorMessage } from './logger';

let deviceInfo: DeviceInfo | undefined;

export const setDeviceInfo = async (): Promise<DeviceInfo | undefined> => {
  if (typeof window !== 'undefined') {
    try {
      deviceInfo = await window.__PLATFORM__.getDeviceInfo?.();
      return deviceInfo;
    } catch (err: unknown) {
      AppLogger.warn({
        message: 'Cannot prepare analytics properly.',
        data: {
          error: JSON.stringify(err)
        }
      });
      return undefined;
    }
  }
  return undefined;
};

export const getDeviceInfo = () => deviceInfo;

let deviceMemory: number | undefined;

const fetchMemoryFromLocalStorage = () => {
  const storage = Storage.getLocalStorage({ staleKeys: [], staleTime: 0 });
  const deviceMemoryFromLocalStorage = storage.readSync('DEVICE_MEMORY');
  if (typeof deviceMemoryFromLocalStorage === 'number') {
    deviceMemory = deviceMemoryFromLocalStorage;
  }
  return deviceMemory;
};

export const getDeviceTotalMemory = async (platformData: PlatformAPI) => {
  if (fetchMemoryFromLocalStorage()) {
    return Promise.resolve(deviceMemory);
  }
  const storage = Storage.getLocalStorage({ staleKeys: [], staleTime: 0 });
  if ('getDeviceMemory' in platformData) {
    try {
      deviceMemory = await platformData.getDeviceMemory?.();
      if (typeof deviceMemory === 'number' && deviceMemory !== -1)
        storage.updateSync('DEVICE_MEMORY', deviceMemory);
    } catch (error) {
      logErrorMessage({
        message: 'Error While reading device memory',
        data: { error: (error as Error)?.message || '' }
      });
    }
  }
  return deviceMemory;
};

const GB_IN_BYTES = 1024 * 1024 * 1024;

export const getMemoryInfo = async (): Promise<MemoryInfo | undefined> => {
  const deviceTotalMemory = await getDeviceTotalMemory(window.__PLATFORM__);

  if (!deviceTotalMemory || deviceTotalMemory === -1) {
    return undefined;
  }

  return {
    availableRamMb: 0,
    isMemoryWarningRaised: false,
    availableDiskSpaceGb: Math.floor((deviceTotalMemory / GB_IN_BYTES) * 100) / 100
  };
};

export const getIsDarkModeEnabled = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }

  return window.matchMedia('(prefers-color-scheme: dark)')?.matches || false;
};

export const getDeviceMemory = () => {
  if (typeof deviceMemory === 'undefined') fetchMemoryFromLocalStorage();
  return deviceMemory;
};

export const getApiPlatform = () => {
  let platform = Platform.WEBOS;
  if (typeof window !== 'undefined') {
    platform = window.__PLATFORM__.name;
  }

  // Below listed platforms will fallback to WebOS
  const webOSFallbackPlatforms = [
    Platform.WEB,
    Platform.MCA,
    Platform.AIRTEL,
    Platform.VIDAA,
    Platform.ASTRO,
    Platform.COOLITA,
    Platform.SKY
  ];

  return webOSFallbackPlatforms.includes(platform) ? Platform.WEBOS : platform;
};

export const getPlatform = () => window.__PLATFORM__.name;

export const getIsTrayFocusMovable = () =>
  typeof window !== 'undefined' && [Platform.WEBOS].includes(window.__PLATFORM__.name);

export const isObject = (input: unknown): input is Record<PropertyKey, unknown> =>
  input !== null && typeof input === 'object' && !Array.isArray(input);

export const getUserAgent = (): string => {
  if (typeof window !== 'undefined') {
    return navigator.userAgent;
  }
  return '';
};

export const getBrandName = () => deviceInfo?.manufacturer || 'Unknown';

export const getModelName = () => deviceInfo?.modelName || 'Unknown';

export enum PlaybackConfigKeys {
  enableDebuggerMode = 'web.playback.enableDebuggerMode'
}
